import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { getConfigOptions } from "~/store/actions/actions.configOptions.jsx";
import { addReminder, editReminder } from "~/store/actions/actions.reminders.jsx";
import { getPossibleSubscribers } from "~/store/selectors/selector.subscription";
import { getLoggedUserId } from "~/store/selectors/selector.users.jsx";
import { getNextWorkingDay } from "~/store/selectors/selector.workload";
import { GeneralReminderForm } from "./Forms/GeneralReminderForm";
import { RELATED_FIELD_BY_PARENT_TYPE } from "./utils";

const ReminderFormContainer = ({ id, parent, members, onCancel, onSubmit, open = false, recipients = null }) => {
  const dispatch = useDispatch();
  const nextWorkingDay = useSelector((state) => getNextWorkingDay(state));
  const possibleSubscribers = useSelector((state) => getPossibleSubscribers(state, members));
  const loggedUserId = useSelector(getLoggedUserId);

  const selectedRecipients = useMemo(() => {
    if (Array.isArray(recipients)) {
      return recipients;
    }
    return [loggedUserId];
  }, [loggedUserId, recipients]);

  useEffect(() => {
    dispatch(getConfigOptions(["time_first_week_day"]));
  }, [dispatch]);

  const handleSubmit = useCallback(
    (attributes) => {
      if (id) {
        dispatch(editReminder(id, { ...attributes })).then(() => {
          if (typeof onSubmit === "function") {
            onSubmit();
          }
        });
      } else {
        dispatch(
          addReminder(parent.id, parent.class, { ...attributes, parent_type: parent.class, parent_id: parent.id })
        ).then((result) => {
          if (typeof onSubmit === "function") {
            onSubmit(result);
          }
        });
      }
    },
    [id, dispatch, onSubmit, parent]
  );

  return (
    <GeneralReminderForm
      id={id}
      onCancel={onCancel}
      onSubmit={handleSubmit}
      parent={parent}
      relatedField={RELATED_FIELD_BY_PARENT_TYPE[parent.class]}
      recipients={selectedRecipients}
      nextWorkingDay={nextWorkingDay}
      members={possibleSubscribers}
      open={open}
    />
  );
};

ReminderFormContainer.propTypes = {
  id: PropTypes.number,
  parent: PropTypes.object.isRequired,
  members: PropTypes.array.isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  open: PropTypes.bool,
  recipients: PropTypes.array,
};

ReminderFormContainer.defaultProps = {
  onCancel: () => null,
  onSubmit: () => null,
};

export default ReminderFormContainer;
